import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import api from "../lib/api.ts";
import NotificationBar from "../components/home/NotifictionBar.jsx";
import { useLanguage } from "../context/LanguageContext";

const BlogDetail = () => {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogDetail = async () => {
      try {
        setLoading(true);

        // First, fetch all blogs to determine pagination
        const allBlogsResponse = await api.get("/blogs");
        let allBlogs = [];

        if (allBlogsResponse.data && allBlogsResponse.data.success) {
          allBlogs = allBlogsResponse.data.data || [];
        } else if (Array.isArray(allBlogsResponse.data)) {
          allBlogs = allBlogsResponse.data;
        }

        // Sort blogs by creation date (newest first)
        allBlogs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        // Find current blog index
        const currentBlogIndex = allBlogs.findIndex(
          (blog) => blog.slug === slug
        );

        if (currentBlogIndex === -1) {
          setError(translate("blogPage.detail.notFound"));
          return;
        }

        // Get current blog
        const currentBlog = allBlogs[currentBlogIndex];

        // Determine previous and next articles
        currentBlog.previousArticle =
          currentBlogIndex < allBlogs.length - 1
            ? allBlogs[currentBlogIndex + 1]
            : null;
        currentBlog.nextArticle =
          currentBlogIndex > 0 ? allBlogs[currentBlogIndex - 1] : null;

        setBlog(currentBlog);
      } catch (error) {
        console.error("Error fetching blog detail:", error);
        setError(translate("blog.detail.notFound"));
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlogDetail();
    }
  }, [slug]);

  if (loading) {
    return (
      <div
        className='min-h-screen flex items-center justify-center p-4'
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        <div className='text-center'>
          <div className='animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto'></div>
          <p className='mt-4 text-sm sm:text-base text-gray-600'>
            {translate("blogPage.detail.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div
        className='min-h-screen flex items-center justify-center p-4'
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        <div className='text-center'>
          <h1 className='text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4'>
            {translate("blogPage.detail.notFound")}
          </h1>
          <p className='text-sm sm:text-base text-gray-600 mb-6 sm:mb-8'>
            {translate("blogPage.detail.notFoundMessage")}
          </p>
          <Link
            to='/blog'
            className='bg-red-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base'
          >
            {translate("blogPage.detail.backToBlog")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className='min-h-screen bg-white'
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{blog?.meta_title || blog?.title || "Blog Post"}</title>
        <meta name='description' content={blog?.meta_description || ""} />
        <meta name='keywords' content={blog?.meta_keywords || ""} />
        <meta
          name='robots'
          content={
            typeof blog?.meta_robots === "boolean"
              ? blog.meta_robots
                ? "index,follow"
                : "noindex,nofollow"
              : blog?.meta_robots || "index,follow"
          }
        />

        {/* Open Graph (Facebook/LinkedIn) */}
        <meta
          property='og:title'
          content={blog?.og_title || blog?.title || ""}
        />
        <meta
          property='og:description'
          content={blog?.og_description || blog?.meta_description || ""}
        />
        <meta property='og:type' content='article' />
        {blog?.featured_image && (
          <meta property='og:image' content={blog.featured_image} />
        )}

        {/* Twitter Cards */}
        <meta
          name='twitter:card'
          content={blog?.featured_image ? "summary_large_image" : "summary"}
        />
        <meta
          name='twitter:title'
          content={blog?.og_title || blog?.title || ""}
        />
        <meta
          name='twitter:description'
          content={blog?.og_description || blog?.meta_description || ""}
        />
        {blog?.featured_image && (
          <meta name='twitter:image' content={blog.featured_image} />
        )}
      </Helmet>

      {/* Blog Header */}
      <NotificationBar />
      <div className='relative bg-[#010101BF] overflow-hidden'>
        {/* Background Geometric Shapes */}
        <div className='absolute inset-0 hidden sm:block'>
          {/* Left side shapes */}
          <div className='absolute left-4 sm:left-10 top-12 sm:top-20 w-8 h-8 sm:w-16 sm:h-16 bg-white/10 rounded-full'></div>
          <div className='absolute left-8 sm:left-20 top-16 sm:top-32 w-4 h-4 sm:w-8 sm:h-8 bg-[#E95923]/20 transform rotate-45'></div>
          <div className='absolute left-12 sm:left-32 top-8 sm:top-16 w-6 h-6 sm:w-12 sm:h-12 border-2 border-white/20 rounded-full'></div>

          {/* Right side shapes */}
          <div className='absolute right-8 sm:right-20 top-8 sm:top-16 w-12 sm:w-20 h-2 sm:h-4 bg-[#E95923]/30'></div>
          <div className='absolute right-4 sm:right-10 top-14 sm:top-28 w-10 sm:w-16 h-2 sm:h-4 bg-white/20'></div>
          <div className='absolute right-12 sm:right-32 top-12 sm:top-24 w-1 sm:w-2 h-8 sm:h-16 bg-white/30'></div>
          <div className='absolute right-6 sm:right-16 top-20 sm:top-40 w-1 sm:w-2 h-6 sm:h-12 bg-[#E95923]/40'></div>
        </div>

        <div className='relative z-10 max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 md:py-12'>
          <div
            className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6 ${
              isRTL ? "sm:flex-row-reverse" : ""
            }`}
          >
            <Link
              to='/blog'
              className={`inline-flex items-center text-white hover:text-gray-300 transition-colors font-medium text-xs sm:text-sm bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-white/20 ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <svg
                className={`w-3 h-3 sm:w-4 sm:h-4 ${
                  isRTL ? "ml-1.5 sm:ml-2 rotate-180" : "mr-1.5 sm:mr-2"
                }`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
              <span className='hidden sm:inline'>
                {translate("blogPage.detail.backToBlog")}
              </span>
              <span className='sm:hidden'>
                {translate("blogPage.detail.back")}
              </span>
            </Link>
            <div className='flex items-center text-xs sm:text-sm text-gray-300 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-white/20'>
              <span>{blog.createdBy}</span>
            </div>
          </div>
          <h1 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 tracking-tight ${
            isRTL ? "text-right" : "text-left"
          }`}>
            {blog.title}
          </h1>
          <div className={`text-xs sm:text-sm text-gray-300 ${
            isRTL ? "text-right" : "text-left"
          }`}>
            {blog.createdAt &&
              new Date(blog.createdAt).toLocaleDateString(
                isRTL ? "ar-AE" : "en-US"
              )}
          </div>
        </div>
      </div>

      {/* Blog Content */}
      <div
        className={`max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 ${
          blog.featured_image ? "py-8 sm:py-12 md:py-16" : "py-6 sm:py-8"
        }`}
      >
        <article className='bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg sm:shadow-xl overflow-hidden'>
          {/* Featured Image */}
          {blog.featured_image && (
            <div className='relative h-[250px] sm:h-[300px] md:h-[350px] lg:h-[400px] w-full overflow-hidden'>
              <img
                src={blog.featured_image}
                alt={blog.title}
                className='w-full h-full object-cover'
              />
              <div className='absolute inset-0 bg-gradient-to-t from-black/20 to-transparent'></div>
            </div>
          )}

          {/* Blog Content - Adjust padding based on image presence */}
          <div
            className={`${
              blog.featured_image
                ? "p-4 sm:p-6 md:p-8 lg:p-12"
                : "p-4 sm:p-5 md:p-6"
            }`}
          >
            <style>{`
              /* Ensure Quill-generated lists render correctly */
              .blog-content .ql-ui { display: none; }
              .blog-content ol { 
                list-style: decimal; 
                ${isRTL ? "margin-right: 1rem; margin-left: 0;" : "margin-left: 1rem; margin-right: 0;"}
              }
              .blog-content ul { 
                list-style: disc; 
                ${isRTL ? "margin-right: 1rem; margin-left: 0;" : "margin-left: 1rem; margin-right: 0;"}
              }
              @media (min-width: 640px) {
                .blog-content ol, .blog-content ul { 
                  ${isRTL ? "margin-right: 1.25rem; margin-left: 0;" : "margin-left: 1.25rem; margin-right: 0;"}
                }
              }
              .blog-content li { margin: 0.375rem 0; }
              .blog-content li[data-list="bullet"] { list-style-type: disc; }
              .blog-content li[data-list="ordered"] { list-style-type: decimal; }
              .blog-content h1, .blog-content h2, .blog-content h3 { 
                color: #111827 !important; 
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
                margin: 0.75em 0 0.5em 0 !important;
                line-height: 1.25 !important;
                font-weight: 600 !important;
                ${isRTL ? "text-align: right;" : "text-align: left;"}
              }
              @media (min-width: 640px) {
                .blog-content h1, .blog-content h2, .blog-content h3 {
                  margin: 1em 0 !important;
                }
              }
              .blog-content h1 { font-size: 1.5em !important; font-weight: 700 !important; }
              @media (min-width: 640px) {
                .blog-content h1 { font-size: 2em !important; }
              }
              .blog-content h2 { font-size: 1.25em !important; }
              @media (min-width: 640px) {
                .blog-content h2 { font-size: 1.5em !important; }
              }
              .blog-content h3 { font-size: 1.1em !important; }
              @media (min-width: 640px) {
                .blog-content h3 { font-size: 1.25em !important; }
              }
              /* Normalize inner spans possibly carrying inline sizes from CMS */
              .blog-content h1 span,
              .blog-content h2 span,
              .blog-content h3 span { font-size: inherit !important; font-weight: inherit !important; }
              .blog-content p { 
                color: #374151; 
                font-size: 0.875rem; 
                line-height: 1.75; 
                ${isRTL ? "text-align: right;" : "text-align: left;"}
              }
              @media (min-width: 640px) {
                .blog-content p { font-size: 1rem; }
              }
              @media (min-width: 1024px) {
                .blog-content p { font-size: 1.125rem; }
              }
              .blog-content a { color: #2563eb; text-decoration: underline; }
              .blog-content img { max-width: 100%; height: auto; border-radius: 0.5rem; margin: 1rem 0; }
              .blog-content blockquote {
                ${isRTL ? "border-right: 4px solid #bfdbfe; padding-right: 1rem; padding-left: 0; margin-right: 0; margin-left: 1rem;" : "border-left: 4px solid #bfdbfe; padding-left: 1rem; padding-right: 0; margin-left: 0; margin-right: 1rem;"}
              }
            `}</style>
            <div
              className={`blog-content prose prose-sm sm:prose-base lg:prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-blue-600 prose-strong:text-gray-900 prose-blockquote:border-blue-200 prose-blockquote:bg-blue-50 prose-ul:text-gray-700 prose-ol:text-gray-700 prose-li:text-gray-700 prose-li:marker:text-gray-500 ${
                isRTL ? "text-right" : "text-left"
              }`}
              style={{
                "--tw-prose-headings": "#111827",
                "--tw-prose-body": "#374151",
                "--tw-prose-links": "#2563eb",
                "--tw-prose-bold": "#111827",
                "--tw-prose-lists": "#374151",
                "--tw-prose-list-markers": "#6b7280",
                direction: isRTL ? "rtl" : "ltr",
              }}
              dir={isRTL ? "rtl" : "ltr"}
              dangerouslySetInnerHTML={{ __html: blog.description }}
            />
          </div>
        </article>

        {/* Pagination Navigation */}
        <div
          className={`flex flex-col sm:flex-row justify-between items-stretch sm:items-start gap-3 sm:gap-4 ${
            blog.featured_image ? "mt-6 sm:mt-8 md:mt-12" : "mt-6 sm:mt-8"
          } ${isRTL ? "sm:flex-row-reverse" : ""}`}
        >
          {/* Previous Article */}
          <div
            className={`flex-1 flex w-full sm:w-auto ${
              isRTL ? "justify-end" : "justify-start"
            }`}
          >
            {blog.previousArticle ? (
              <Link
                to={`/blog/${blog.previousArticle.slug}`}
                className={`inline-flex items-center bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg sm:rounded-xl hover:from-[#FF3A23] hover:to-[#FFA73D] transition-all duration-200 font-semibold shadow-lg hover:shadow-xl w-full sm:w-auto sm:min-w-[180px] text-xs sm:text-sm ${
                  isRTL
                    ? "justify-center sm:justify-end flex-row-reverse"
                    : "justify-center sm:justify-start"
                }`}
              >
                <svg
                  className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                    isRTL ? "ml-1.5 sm:ml-2 rotate-180" : "mr-1.5 sm:mr-2"
                  }`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 19l-7-7 7-7'
                  />
                </svg>
                <span className='font-medium'>
                  {translate("blogPage.detail.previousArticle")}
                </span>
              </Link>
            ) : (
              <button
                disabled
                className={`inline-flex items-center bg-gray-400 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg sm:rounded-xl cursor-not-allowed font-semibold shadow-lg opacity-50 w-full sm:w-auto sm:min-w-[180px] text-xs sm:text-sm ${
                  isRTL
                    ? "justify-center sm:justify-end flex-row-reverse"
                    : "justify-center sm:justify-start"
                }`}
              >
                <svg
                  className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                    isRTL ? "ml-1.5 sm:ml-2 rotate-180" : "mr-1.5 sm:mr-2"
                  }`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 19l-7-7 7-7'
                  />
                </svg>
                <span className='font-medium'>
                  {translate("blogPage.detail.previousArticle")}
                </span>
              </button>
            )}
          </div>

          {/* Next Article */}
          <div
            className={`flex-1 flex w-full sm:w-auto ${
              isRTL ? "justify-start" : "justify-end"
            }`}
          >
            {blog.nextArticle ? (
              <Link
                to={`/blog/${blog.nextArticle.slug}`}
                className={`inline-flex items-center bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg sm:rounded-xl hover:from-[#FF3A23] hover:to-[#FFA73D] transition-all duration-200 font-semibold shadow-lg hover:shadow-xl w-full sm:w-auto sm:min-w-[180px] text-xs sm:text-sm ${
                  isRTL
                    ? "justify-center sm:justify-start flex-row-reverse"
                    : "justify-center sm:justify-end"
                }`}
              >
                <span className='font-medium'>
                  {translate("blogPage.detail.nextArticle")}
                </span>
                <svg
                  className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                    isRTL ? "mr-1.5 sm:mr-2 rotate-180" : "ml-1.5 sm:ml-2"
                  }`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 5l7 7-7 7'
                  />
                </svg>
              </Link>
            ) : (
              <button
                disabled
                className={`inline-flex items-center bg-gray-400 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg sm:rounded-xl cursor-not-allowed font-semibold shadow-lg opacity-50 w-full sm:w-auto sm:min-w-[180px] text-xs sm:text-sm ${
                  isRTL
                    ? "justify-center sm:justify-start flex-row-reverse"
                    : "justify-center sm:justify-end"
                }`}
              >
                <span className='font-medium'>
                  {translate("blogPage.detail.nextArticle")}
                </span>
                <svg
                  className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                    isRTL ? "mr-1.5 sm:mr-2 rotate-180" : "ml-1.5 sm:ml-2"
                  }`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 5l7 7-7 7'
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
